/**
 * AdminOpsCampusVideos — CMS for the 90-second campus tour attached
 * to each university.
 *
 * Ops flow:
 *   1. Toggle between "Seeded" (11 unis with a video) and "Unseeded"
 *      (everything else). Search filter narrows large lists.
 *   2. For each row: type/paste a YouTube URL (or raw MP4 URL) +
 *      a provenance note describing the source channel.
 *   3. Save → server runs the SAME verifier the seed script uses.
 *      If the URL is dead, embed-blocked, or comes from a
 *      non-official channel, the save fails with the exact
 *      verifier message shown inline. Nothing broken lands in prod.
 *
 * Endpoints
 *   GET  /api/admin/ops/campus-videos          — list all
 *   PUT  /api/admin/ops/campus-videos/{uni_id} — save
 *   DELETE /api/admin/ops/campus-videos/{uni_id} — clear
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Trash2, Save, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";


export default function AdminOpsCampusVideos() {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("seeded");   // "seeded" | "unseeded"
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  // Per-row draft state so we don't overwrite server state as the
  // admin types. `drafts[uni_id]` → { url, provenance }.
  const [drafts, setDrafts] = useState({});

  async function load() {
    try {
      const r = await api.get("/admin/ops/campus-videos");
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load — check admin auth.");
    }
  }
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const src = bucket === "seeded" ? data.seeded : data.unseeded;
    const needle = q.trim().toLowerCase();
    if (!needle) return src;
    return src.filter((r) =>
      (r.name || "").toLowerCase().includes(needle)
      || (r.uni_id || "").toLowerCase().includes(needle)
      || (r.city || "").toLowerCase().includes(needle)
    );
  }, [data, bucket, q]);

  function updateDraft(uni_id, key, value) {
    setDrafts((d) => ({
      ...d,
      [uni_id]: { ...(d[uni_id] || {}), [key]: value },
    }));
  }

  async function save(row) {
    const draft = drafts[row.uni_id] || {};
    const url = (draft.url ?? row.campus_video_url ?? "").trim();
    const provenance = (draft.provenance ?? row.provenance ?? "").trim();
    if (!url) {
      toast.error("Paste a YouTube or MP4 URL first.");
      return;
    }
    if (!provenance || provenance.length < 6) {
      toast.error("Provenance is required — describe the source channel.");
      return;
    }
    setBusy((b) => ({ ...b, [row.uni_id]: true }));
    try {
      const r = await api.put(
        `/admin/ops/campus-videos/${encodeURIComponent(row.uni_id)}`,
        { url, provenance },
      );
      toast.success(`Saved · ${r.data.verifier_message}`);
      // Clear draft, reload so the row moves buckets if needed.
      setDrafts((d) => { const n = { ...d }; delete n[row.uni_id]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.uni_id]; return n; });
    }
  }

  async function clear(row) {
    if (!row.campus_video_url) return;
    if (!window.confirm(`Remove the campus tour video for ${row.name}?`)) return;
    setBusy((b) => ({ ...b, [row.uni_id]: true }));
    try {
      await api.delete(`/admin/ops/campus-videos/${encodeURIComponent(row.uni_id)}`);
      toast.success(`Cleared ${row.name}`);
      setDrafts((d) => { const n = { ...d }; delete n[row.uni_id]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed.");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.uni_id]; return n; });
    }
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F3F1EC]"
        data-testid="admin-ops-campus-videos-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-stone-500" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F3F1EC] py-10 px-6"
      data-testid="admin-ops-campus-videos"
    >
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 border-b border-stone-200 pb-6">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/admin" className="text-xs font-underground-sc tracking-widest text-stone-500 hover:text-stone-900">
              ← back to admin
            </Link>
          </div>
          <h1 className="font-editorial text-3xl text-[#002147]">Campus tour videos</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            Manage the 90-second campus tour attached to every
            university. YouTube (`watch?v=…`) and raw MP4 URLs both
            work. Every save runs the same verifier the release
            script uses: dead videos, embed-blocked uploads, and
            channel drift are all rejected before the URL hits the
            DB.
          </p>
          <div
            className="mt-4 flex items-center gap-3 flex-wrap"
            data-testid="admin-ops-campus-videos-totals"
          >
            <button
              onClick={() => setBucket("seeded")}
              className={`px-3 py-1.5 text-[11px] font-underground-sc tracking-widest transition ${
                bucket === "seeded"
                  ? "bg-[#002147] text-[#F3F1EC]"
                  : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
              }`}
              data-testid="admin-ops-tab-seeded"
            >
              Seeded · {data.total_seeded}
            </button>
            <button
              onClick={() => setBucket("unseeded")}
              className={`px-3 py-1.5 text-[11px] font-underground-sc tracking-widest transition ${
                bucket === "unseeded"
                  ? "bg-[#002147] text-[#F3F1EC]"
                  : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
              }`}
              data-testid="admin-ops-tab-unseeded"
            >
              Unseeded · {data.total_unseeded}
            </button>
            <Input
              placeholder="Filter by name / city / id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-72 h-8 text-[12px] ml-auto"
              data-testid="admin-ops-campus-videos-search"
            />
          </div>
        </div>

        <ul className="bg-white border border-stone-200">
          {rows.length === 0 ? (
            <li className="px-5 py-8 text-center text-stone-500 text-sm">
              {q ? "No matches for that filter." : "Nothing in this bucket."}
            </li>
          ) : (
            rows.slice(0, 200).map((row) => (
              <VideoRow
                key={row.uni_id}
                row={row}
                draft={drafts[row.uni_id] || {}}
                onDraft={(k, v) => updateDraft(row.uni_id, k, v)}
                onSave={() => save(row)}
                onClear={() => clear(row)}
                busy={!!busy[row.uni_id]}
              />
            ))
          )}
          {rows.length > 200 && (
            <li className="px-5 py-3 border-t border-stone-100 text-center text-[11px] font-mono text-stone-500">
              Showing first 200 of {rows.length} — narrow the filter to see the rest.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}


function VideoRow({ row, draft, onDraft, onSave, onClear, busy }) {
  const url = draft.url ?? row.campus_video_url ?? "";
  const provenance = draft.provenance ?? row.provenance ?? "";
  const dirty =
    (draft.url !== undefined && draft.url !== (row.campus_video_url ?? ""))
    || (draft.provenance !== undefined && draft.provenance !== (row.provenance ?? ""));

  return (
    <li
      className="px-5 py-4 border-b border-stone-100 last:border-0"
      data-testid={`admin-ops-campus-video-row-${row.uni_id}`}
    >
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="font-medium text-stone-900 truncate">
            {row.name}
            <span className="ml-2 font-mono text-[10px] text-stone-400">
              {row.uni_id}
            </span>
          </div>
          <div className="text-[12px] text-stone-500 truncate">
            {row.city || "—"} · {row.country || "—"}
            {row.stamped_at && (
              <span className="ml-2 font-mono text-[10px] text-stone-400">
                last saved {new Date(row.stamped_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        {row.campus_video_url && (
          <a
            href={row.campus_video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-mono text-indigo-700 hover:underline inline-flex items-center gap-1 shrink-0"
            data-testid={`admin-ops-campus-video-preview-${row.uni_id}`}
          >
            preview <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          value={url}
          onChange={(e) => onDraft("url", e.target.value)}
          placeholder="https://www.youtube.com/watch?v=… or https://…mp4"
          disabled={busy}
          className="h-9 text-[12px] font-mono"
          data-testid={`admin-ops-campus-video-url-${row.uni_id}`}
        />
        <Input
          value={provenance}
          onChange={(e) => onDraft("provenance", e.target.value)}
          placeholder="youtube.com/@officialchannel — 'Video title'"
          disabled={busy}
          className="h-9 text-[12px]"
          data-testid={`admin-ops-campus-video-provenance-${row.uni_id}`}
        />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          onClick={onSave}
          disabled={busy || !dirty}
          size="sm"
          className="h-8 text-[11px] font-underground-sc tracking-widest"
          data-testid={`admin-ops-campus-video-save-${row.uni_id}`}
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <><Save className="h-3 w-3 mr-1.5" /> Save</>
          )}
        </Button>
        {row.campus_video_url && (
          <Button
            onClick={onClear}
            disabled={busy}
            size="sm"
            variant="ghost"
            className="h-8 text-[11px] font-underground-sc tracking-widest text-red-700 hover:text-red-900 hover:bg-red-50"
            data-testid={`admin-ops-campus-video-clear-${row.uni_id}`}
          >
            <Trash2 className="h-3 w-3 mr-1.5" /> Remove
          </Button>
        )}
        {dirty && (
          <span className="ml-auto text-[10px] font-underground-sc tracking-widest text-amber-700">
            unsaved
          </span>
        )}
      </div>
    </li>
  );
}
